import { Box, Collapse, Divider, Stack, Typography } from "@mui/material";
import React, { useEffect } from "react";
import {
  CheckboxFormField,
  DateFormField,
  FileFormField,
  RadioFormField,
  TextFormField,
} from "../common/components/FormFields";
import { useField, useForm } from "react-final-form";
import { LightBanner } from "./LightBanner";

const GUARDIAN = "The guardian";

export interface ConsentAndAuthorizationProps {}

export function ConsentAndAuthorization({}: ConsentAndAuthorizationProps) {
  const form = useForm();
  const {
    input: { value: applicantType },
  } = useField<string>("applicantType", { subscription: { value: true } });

  const isGuardian = applicantType === GUARDIAN;

  // final-form retains values of unregistered fields, so collapsing the section
  // is not enough to keep an already uploaded file out of the submission.
  useEffect(() => {
    if (!isGuardian) {
      form.change("proofOfGuardianship", undefined);
    }
  }, [isGuardian, form]);

  return (
    <Box>
      <Typography variant="h5">Consent and Authorization</Typography>
      <Stack gap={2} py={2}>
        <Box>
          <RadioFormField
            name="applicantType"
            label="I am applying as"
            required
            options={[
              "The parent",
              "The child at the age of consent",
              GUARDIAN,
            ]}
            noIcon
          />
        </Box>
        <Collapse in={isGuardian} unmountOnExit>
          <LightBanner mb={2}>
            Please provide documentation to confirm you are a guardian.
            Documentation may include proof of formal or informal adoption or
            kinship/care arrangements including a signed letter from the child's
            parent, Court Order, Last Will and Testament, or Power of Attorney.
          </LightBanner>
          <FileFormField
            name="proofOfGuardianship"
            label="Proof of Guardianship"
            multiFile
            required
            noIcon
          />
        </Collapse>
        <Typography>
          I have read the above statements and understand what personal
          information is collected and how the information may be used to
          review, process, administer, or refer this request under the Inuit
          Child First Initiative. I understand I can withdraw this consent by
          contacting{" "}
          <a href="mailto:inuitcfi@inuvialuit.com">inuitcfi@inuvialuit.com</a>.
        </Typography>
        <Box>
          <Typography variant="caption" color="text.secondary">
            All four checkboxes below are required.
          </Typography>
          <Stack border={1} borderColor="divider" divider={<Divider />}>
            <Box px={2} py={1}>
              <CheckboxFormField
                name="includedAllInformation"
                label="I confirm that all included information is true and accurate to the best of my knowledge."
                noIcon
                mustBeTrue
              />
            </Box>
            <Box px={2} py={1}>
              <CheckboxFormField
                name="pendingApplicationsDisclosed"
                label="I confirm that I have accurately disclosed whether I have a pending Inuit Child First Initiative application."
                noIcon
                mustBeTrue
              />
            </Box>
            <Box px={2} py={1}>
              <CheckboxFormField
                name="authorizeSharing"
                label="I authorize IRC to share information from this application with Inuit Child First Initiative where needed to review, verify, process, administer, or refer this request."
                noIcon
                mustBeTrue
              />
            </Box>
            <Box px={2} py={1}>
              <CheckboxFormField
                name="informationTrue"
                label="I understand that if any information I provide is inaccurate or untrue, it may need to be verified, and my request may be denied."
                noIcon
                mustBeTrue
              />
            </Box>
          </Stack>
        </Box>
        <Box>
          <Typography>
            By typing my full name below, I confirm that I have read and
            understood this form and acknowledge that my typed name has the
            same legal effect as an electronic signature.
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} gap={1} pt={1}>
            <Box flex={1}>
              <TextFormField
                name="fullName"
                label="Full Name"
                required
                noIcon
              />
            </Box>
            <Box flex={1}>
              <DateFormField
                name="dateSigned"
                label="Date"
                required
                noIcon
                stringOutput
              />
            </Box>
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
}
